/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ISubscription } from "./ISubscription";

/**
 * Generic pub/sub implementation used by all managers for state notifications.
 * Replaces TivoSubscriptionModel from the legacy codebase.
 */
export class SubscriptionModel<T> implements ISubscription<T> {
  private mSubscribers: Array<(data: T) => void> = [];

  /**
   * Notify all subscribers with the given data.
   * Iterates over a snapshot of the subscriber list to safely handle
   * unsubscribes that occur during notification.
   * @param data - The data to broadcast.
   */
  public notify(data: T): void {
    const subscribers = [...this.mSubscribers];
    for (let i = 0; i < subscribers.length; i++) {
      subscribers[i](data);
    }
  }

  /**
   * Register a callback to receive notifications.
   * Duplicate registrations are ignored.
   * @param callback - The function invoked on each notification.
   */
  public subscribe(callback: (data: T) => void): void {
    if (this.mSubscribers.indexOf(callback) === -1) {
      this.mSubscribers.push(callback);
    }
  }

  /**
   * Remove a previously registered callback.
   * If the callback is not found, this is a no-op.
   * @param callback - The exact function reference passed to subscribe.
   */
  public unsubscribe(callback: (data: T) => void): void {
    const index = this.mSubscribers.indexOf(callback);
    if (index !== -1) {
      this.mSubscribers.splice(index, 1);
    }
  }
}
